import { createRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Loader2, XCircle } from "lucide-react";
import { useSession, authClient } from "../auth-client";
import { useInvitationQuery } from "@/frontend/queries/invite";
import { InviteShell } from "@/frontend/components/invite/InviteShell";
import { NameOnlyFlow } from "@/frontend/components/invite/NameOnlyFlow";
import { OnboardingFlow } from "@/frontend/components/invite/OnboardingFlow";
import type { InvitationDetails } from "@/frontend/types/invite";
import { Button } from "@/frontend/components/ui/button";
import {
  useConfirmInvitationIdentityMutation,
  useRecordInviteActivityMutation,
} from "@/frontend/mutations/invite";
import {
  clearInviteResumeId,
  rememberInviteResumeId,
} from "@/frontend/utils/invite-flow";

import { Route as rootRoute } from "./__root";

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invite/accept",
  validateSearch: (search: Record<string, unknown>) => ({
    id: (search.id as string) || "",
  }),
  component: InviteAcceptPage,
});

// ─── Main page ────────────────────────────────────────────────────────────────

function InviteAcceptPage() {
  const { id: invitationId } = Route.useSearch();
  const { data: session, isPending: sessionPending } = useSession();
  const [signingOut, setSigningOut] = useState(false);
  const confirmIdentityMutation = useConfirmInvitationIdentityMutation();
  const recordInviteActivityMutation = useRecordInviteActivityMutation();
  const hasRecordedOpen = useRef(false);
  const hasConfirmedIdentity = useRef(false);
  // Track first-load completion so background refetches (e.g. on tab focus —
  // Better Auth's useSession flips isPending back to true when data is null)
  // don't replace the rendered form with a spinner and unmount it.
  const hasLoadedOnce = useRef(false);

  // Fetch invitation details (public — works before login)
  const inviteQuery = useInvitationQuery(invitationId);

  const invitation = inviteQuery.data;
  const inviteError = inviteQuery.error;
  if (invitation || inviteError) hasLoadedOnce.current = true;

  useEffect(() => {
    if (!invitationId || !invitation || invitation.status !== "pending") {
      if (invitationId) clearInviteResumeId(invitationId);
      return;
    }
    rememberInviteResumeId(invitationId);
    if (hasRecordedOpen.current) return;
    hasRecordedOpen.current = true;
    recordInviteActivityMutation.mutate({
      invitationId,
      activityType: "invite_opened",
      status: "success",
      flow: "invite-accept",
    });
  }, [invitation, invitationId, recordInviteActivityMutation]);

  useEffect(() => {
    if (!session || !invitation || session.user.email !== invitation.email) return;
    if (hasConfirmedIdentity.current || confirmIdentityMutation.isPending || confirmIdentityMutation.isError) return;
    hasConfirmedIdentity.current = true;
    confirmIdentityMutation.mutate(
      { invitationId },
      {
        onError: () => {
          hasConfirmedIdentity.current = false;
        },
      },
    );
  }, [confirmIdentityMutation, invitation, invitationId, session]);

  // ── Loading states ──────────────────────────────────────────────────────────
  if (!invitationId) {
    clearInviteResumeId();
    return (
      <InviteShell>
        <div className="text-center">
          <div className="mx-auto mb-6 flex size-16 items-center justify-center rounded-full bg-destructive/10">
            <XCircle className="size-8 text-destructive" />
          </div>
          <h1 className="mb-2 text-2xl font-bold">Invalid invitation link</h1>
          <p className="mb-6 text-sm text-muted-foreground">
            This invitation link is missing required information. Please ask
            your administrator to resend the invitation.
          </p>
          <Button onClick={() => { window.location.href = "/"; }}>Go to sign in</Button>
        </div>
      </InviteShell>
    );
  }

  if (!hasLoadedOnce.current && (sessionPending || inviteQuery.isPending)) {
    return (
      <InviteShell>
        <div className="flex justify-center">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      </InviteShell>
    );
  }

  if (inviteError || !invitation) {
    clearInviteResumeId(invitationId);
    return (
      <InviteShell>
        <div className="text-center">
          <div className="mx-auto mb-6 flex size-16 items-center justify-center rounded-full bg-destructive/10">
            <XCircle className="size-8 text-destructive" />
          </div>
          <h1 className="mb-2 text-2xl font-bold">Invitation not found</h1>
          <p className="mb-6 text-sm text-muted-foreground">
            This invitation link is invalid, has expired, or has already been
            used. Please ask your administrator to send a new invitation.
          </p>
          <Button onClick={() => { window.location.href = "/"; }}>Go to sign in</Button>
        </div>
      </InviteShell>
    );
  }

  if (invitation.status !== "pending") {
    clearInviteResumeId(invitationId);
    const isExpired = invitation.status === "expired";
    return (
      <InviteShell>
        <div className="text-center">
          <div className="mx-auto mb-6 flex size-16 items-center justify-center rounded-full bg-destructive/10">
            <XCircle className="size-8 text-destructive" />
          </div>
          <h1 className="mb-2 text-2xl font-bold">
            {isExpired ? "Invitation expired" : "Invitation already used"}
          </h1>
          <p className="mb-6 text-sm text-muted-foreground">
            {isExpired
              ? "This invitation has expired. Please ask your administrator to send a new invitation."
              : "This invitation has already been accepted or cancelled. Please ask your administrator to send a new invitation."}
          </p>
          <Button onClick={() => { window.location.href = "/"; }}>Go to sign in</Button>
        </div>
      </InviteShell>
    );
  }

  const invWithHasAccount = invitation as InvitationDetails & { hasAccount?: boolean };
  const hasAccount = !!invWithHasAccount.hasAccount;

  // ── Route to correct flow ───────────────────────────────────────────────────
  // Logged in, email matches: confirm and accept invitation
  // Logged in, email mismatch: auto sign-out, then fall through to invite OTP flow
  // No session: invite OTP flow (locked email, name asked only for new users)
  if (session) {
    if (session.user.email === invitation.email) {
      if (confirmIdentityMutation.isError) {
        return (
          <InviteShell>
            <div className="text-center">
              <div className="mx-auto mb-6 flex size-16 items-center justify-center rounded-full bg-destructive/10">
                <XCircle className="size-8 text-destructive" />
              </div>
              <h1 className="mb-2 text-2xl font-bold">Could not verify invitation</h1>
              <p className="mb-6 text-sm text-muted-foreground">
                We could not confirm that this signed-in account can use this invitation.
                Please sign out and try again with the invited email address.
              </p>
              <Button
                onClick={async () => {
                  await authClient.signOut();
                  window.location.reload();
                }}
              >
                Sign out and retry
              </Button>
            </div>
          </InviteShell>
        );
      }
      if (confirmIdentityMutation.isPending || !hasConfirmedIdentity.current) {
        return (
          <InviteShell>
            <div className="flex justify-center">
              <Loader2 className="size-6 animate-spin text-muted-foreground" />
            </div>
          </InviteShell>
        );
      }
      return (
        <NameOnlyFlow
          invitationId={invitationId}
          invitation={invitation}
          currentName={session.user.name ?? ""}
        />
      );
    }
    // Wrong account — auto sign-out in place; on next render `session` is null
    // and we drop through to the OnboardingFlow (locked invitation email).
    if (!signingOut) {
      setSigningOut(true);
      authClient.signOut().then(() => window.location.reload());
    }
    return (
      <InviteShell>
        <div className="flex justify-center">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      </InviteShell>
    );
  }

  // No session — render the OTP flow with the invitation email locked.
  // Existing users (hasAccount) skip the name step.
  return (
    <OnboardingFlow
      invitationId={invitationId}
      invitation={invitation}
      hasAccount={hasAccount}
    />
  );
}
